import React, { forwardRef, useEffect, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import { cn } from './cn';

function toHtml(content) {
  if (typeof content === 'string') return content;
  if (!content?.ops) return '';
  return new QuillDeltaToHtmlConverter(content.ops, {
    linkRel: 'noreferrer noopener',
    linkTarget: '_blank',
    urlSanitizer: (url) => /^https?:\/\//.test(url) ? url : `http://${url}`,
    inlineStyles: {
      indent: (value, op) => `padding-${op.attributes.direction === 'rtl' ? 'right' : 'left'}:${parseInt(value, 10) * 3}em`,
      list: (value, op) => `padding-${op.attributes.direction === 'rtl' ? 'right' : 'left'}:${parseInt(value, 10) * 3}em`,
      direction: (value, op) => value === 'rtl' ? `direction:rtl${op.attributes.align ? '' : '; text-align: inherit'}` : '',
    },
  }).convert();
}

/** Sanitized HTML / Quill Delta presentation. Saved formatting remains data.
 * Search wraps text nodes only, so a query never rewrites a link or markup. */
const RichTextContent = forwardRef(function RichTextContent({ content, html, query, linkTarget, className, ...props }, forwardedRef) {
  const rootRef = useRef(null);
  const sanitized = useMemo(() => {
    const fragment = DOMPurify.sanitize(html ?? toHtml(content), { RETURN_DOM_FRAGMENT: true });
    fragment.querySelectorAll('a[href]').forEach((link) => {
      if (linkTarget === '_blank' || !link.getAttribute('href').includes('abundowealth.com')) {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      } else {
        link.removeAttribute('target');
        link.removeAttribute('rel');
      }
    });
    const container = document.createElement('div');
    container.appendChild(fragment);
    return container.innerHTML;
  }, [content, html, linkTarget]);

  useEffect(() => {
    const root = rootRef.current;
    root.querySelectorAll('.search-highlight').forEach((span) => span.replaceWith(document.createTextNode(span.textContent)));
    root.normalize();
    const search = query?.trim();
    if (!search) return;
    const expression = new RegExp(search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const matches = [...node.textContent.matchAll(expression)];
      if (!matches.length) return;
      const fragment = document.createDocumentFragment();
      let cursor = 0;
      matches.forEach((match) => {
        fragment.appendChild(document.createTextNode(node.textContent.slice(cursor, match.index)));
        const highlight = document.createElement('span');
        highlight.className = 'search-highlight bg-bun-warning-bg text-bun-text';
        highlight.textContent = match[0];
        fragment.appendChild(highlight);
        cursor = match.index + match[0].length;
      });
      fragment.appendChild(document.createTextNode(node.textContent.slice(cursor)));
      node.replaceWith(fragment);
    });
  }, [query, sanitized]);

  return <div {...props} ref={(node) => {
    rootRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }} className={cn(
    'font-instrument text-bun-body font-book leading-relaxed text-bun-text break-words',
    '[&_p]:my-1 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5',
    '[&_a]:text-bun-blue [&_a]:underline [&_blockquote]:border-l [&_blockquote]:border-bun-line [&_blockquote]:pl-3',
    '[&_h1]:font-instrument [&_h1]:text-bun-heading [&_h1]:leading-snug [&_h1]:tracking-normal',
    '[&_h2]:font-instrument [&_h2]:text-bun-heading [&_h2]:leading-snug [&_h2]:tracking-normal',
    '[&_h3]:font-instrument [&_h3]:text-bun-body [&_h3]:leading-snug [&_h3]:tracking-normal',
    '[&_.current-highlight]:bg-bun-warning [&_.current-highlight]:text-bun-surface',
    '[&_.current-search-match]:bg-bun-ink [&_.current-search-match]:text-bun-on-ink',
    className,
  )} dangerouslySetInnerHTML={{ __html: sanitized }} />;
});

export default RichTextContent;
